<template>
  <div ref="contactComponent" class="m-4 text-center" v-if="isVisible" :class="{ 'loading-size': isVisible }">
    <font-awesome-icon
      :icon="['fa', 'circle-notch']"
      class="fa-spin m-auto spinner fa-4x egx-partner-color"
    />
  </div>
  <div :class="[{'content-box content-box-plus child': !disableCard}, { 'no-card': disableCard }, 'mb-3 m-0']" v-else>
    <div class="card-wrapper">
      <div :class="['card-body', { 'no-card': disableCard }]">
        <div class="form-group row align-items-center mt-2 form">
          <div class="col-12 col-md-4 mb-2 mb-md-0">
            <label for="primaryContact" class="mb-0 field-label" v-if="section !== 'user-profile'">Primary Contact</label>
            <label for="primaryContact" class="mb-0 field-label" v-else>Contact List</label>
          </div>
          <div class="col-12 col-md-3 mb-2 mb-md-0 pl-1">
            <select
              class="form-control input-text"
              :class="isUserEditPage ? 'input-field': ''"
              id="primaryContact"
              v-model="selectedContactId"
              @change="populateForm"
            >
              <option disabled selected value="">Select Contact</option>
              <option v-for="contact in contacts" :key="contact.id" :value="contact.id">{{ getContactName(contact.fields) }}</option>
            </select>
          </div>
          <div
            class="col-12 col-md-5 d-flex justify-content-md-end justify-content-center align-items-end"
            v-if="section == 'user-profile' || (stepConfiguration?.contactpage?.allow_contact_creation ?? true)"
          >
            <button class="btn btn-egx" @click="addNewContact">Add New</button>
          </div>
        </div>
        <div :key="formKey" v-if="showForm" class="mt-4 contact-overview">
          <span class="title">
            <span>Contact Overview</span>
          </span>
          <div class="sub-title" v-safe-html="contactHeader.title"/>
          <p class="description" v-safe-html="contactHeader.subtitle" />
          <ValidationObserver ref="contact">
            <div class="form-group row form" v-for="(field, index) in contactFields" :key="index">
              <label :for="field.name" class="col-4 col-form-label d-flex align-items-center flex-nowrap field-label">
                {{ field.label }}
                <span v-if="(field.required === 'true') ? true : false" class="text-danger">*</span>
              </label>
              <div
                class="col-8 d-flex justify-content-center align-items-center pl-1"
                :class="{ 'col-12 pl-3': windowWidth <= 1200 }"
              >
                <validation-provider
                  :rules="addValidation(field)"
                  v-slot="{ errors }"
                  class="w-100"
                >
                  <input
                    class="form-control input-text"
                    :class="[
                      isUserEditPage ? 'input-field': '',
                      { 'box-alert': errors[0] }
                    ]"
                    v-if="field.type !== 'TEXTAREA' && field.type !== 'EMAIL' && field.type !== 'PHONE'"
                    v-model="newContact[field.name]"
                    :placeholder="field.label"
                    :disabled="isReadOnly"
                    :required="field.required === 'true'"
                  />
                  <input
                    class="form-control input-text"
                    :class="[
                      isUserEditPage ? 'input-field': '',
                      { 'box-alert': errors[0] }
                    ]"
                    type="email"
                    name="email"
                    v-if="field.type === 'EMAIL'"
                    v-model="newContact[field.name]"
                    :placeholder="field.label"
                    :disabled="isReadOnly"
                    :required="field.required === 'true'"
                  />
                  <input
                    class="form-control input-text"
                    :class="[
                      isUserEditPage ? 'input-field': '',
                      { 'box-alert': errors[0] }
                    ]"
                    type="text"
                    v-if="field.type === 'PHONE'"
                    v-model="newContact[field.name]"
                    :placeholder="field.label"
                    :disabled="isReadOnly"
                      @input="formatPhone(field.name)"
                  />
                  <textarea
                    v-else-if="field.type === 'TEXTAREA'"
                    class="form-control input-text"
                    :class="[
                      isUserEditPage ? 'input-field': '',
                      { 'box-alert': errors[0] }
                    ]"
                    :id="field.name"
                    v-model="newContact[field.name]"
                    :placeholder="field.label"
                    :disabled="isReadOnly"
                    :required="field.required === 'true'"
                  ></textarea>
                  <span class="error-message" v-if="errors[0]" :class="errors[0] ? 'has-error' : ''">{{ errors[0] }}</span>
                </validation-provider>
              </div>
            </div>
          </ValidationObserver>
          <div class="form-group row">
            <div class="col-12 d-flex justify-content-center" v-if="isReadOnly">
              <button type="button" class="btn btn-egx" @click="enableEdit">Edit</button>
            </div>
            <div class="col-12 d-flex justify-content-center" v-else-if="!isReadOnly">
              <button type="button" class="btn btn-egx-white mr-2" @click="cancelForm" :disabled="isLoading">Cancel</button>
              <button type="submit" class="btn btn-egx" @click="createContact" :disabled="isLoading">
                <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {{ isLoading ? 'Processing...' : (selectedContactId ? 'Update' : 'Create') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingScreen from './LoadingScreen.vue';
import mockData from '../mockData';
import projectMixins from '@/shared/mixins/projectMixins';

export default {
  components: {
    LoadingScreen,
  },
  mixins: [projectMixins],
  props: {
    section: {
      type: String,
      required: false,
    },
    isUserEditPage: {
      type: Boolean,
      default: false,
    },
    userSelectedContactId: {
      type: String,
      default: '',
    },
    stepConfiguration: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showForm: false,
      newContact: {},
      selectedContactId: this.userSelectedContactId,
      isReadOnly: false,
      loadingScreenContent: {
        title: {
          text: 'Loading Contacts',
          class: [],
        },
        description: {
          text: 'Please wait while we load the contacts',
          class: [],
          style: {},
        },
        spinner: {
          class: ['partner-color'],
          style: {},
        },
        cta: {
          text: '',
          class: ['partner-color'],
          style: {},
          path_name: '',
        },
      },
      isVisible: false,
      disableCard: false,
      isLoading: false,
      contactHeader: mockData.contactHeader,
      contactConfiguration: {},
      formKey: 0,
      windowWidth: window.innerWidth,
    };
  },
  created() {
    this.isVisible = true;
    this.$store.dispatch('contacts/fetchContactsFields').then(() => {
      console.log('Fields created!!');
    });
    this.$store.dispatch('contacts/fetchContacts').then(() => {
      this.$emit('total-contacts', this.contacts.length);

      if (this.contacts.length === 1) {
        this.selectedContactId = this.contacts[0].id;
        this.populateForm();
      } else if (this.selectedContactId) {
        this.populateForm();
      }
      console.log('Contacts loaded!!');
    });
    const project_id = this.$route.params.id;
    if (project_id) {
      this.$store.dispatch('fulfill_integrate/getProjectPrimaryContact', { project_id })
        .then((response) => {
        if (response.contact_id) {
            this.selectedContactId = response.contact_id;
        }
          this.populateForm();
          this.isVisible = false;
        })
        .catch((error) => {
          this.isVisible = false;
          console.log(error);
        });
    } else {
      this.isVisible = false;
    }

    window.addEventListener('resize', this.updateWindowWidth);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowWidth);
  },
  computed: {
    contacts() {
      return this.$store.state.contacts.contacts || [];
    },
    contactFields() {
      const fields = this.updateFieldsAttributes(this.$store.state.contacts.contacts_fields);
      return fields || [];
    },
    settings() {
      return this.$store.state.partners.currentPartner.settings;
    },

    fulfill_sync_details() {
      return this.settings.fulfill_sync_details;
    },
  },
  watch: {
    selectedContactId(newValue) {
      this.$emit('selected-contact-id', newValue);
    },
  },
  methods: {
    getContactName(fields) {
      let contactFields = fields || [];
      if (!fields) {
        const selectedContact = this.contacts.find(contact => contact.id === this.selectedContactId);
        contactFields = selectedContact.fields;
      }
      const firstName = contactFields.find(field => field.name === 'FirstName')?.value || '';
      const lastName = contactFields.find(field => field.name === 'LastName')?.value || '';
      return `${firstName} ${lastName}`;
    },
    populateForm() {
      const selectedContact = this.contacts.find(contact => contact.id === this.selectedContactId);
      if (selectedContact) {
        this.newContact = {};
        selectedContact.fields.forEach(field => {
          this.newContact[field.name] = field.value;
        });
        this.showForm = true;
        this.isReadOnly = true;
      }
    },
    addNewContact() {
      this.selectedContactId = '';
      this.newContact = {};
      this.contactFields.forEach(field => {
        this.newContact[field.name] = '';
      });
      this.showForm = true;
      this.isReadOnly = false;
      this.formKey++;
      this.$nextTick(() => {
        const contactOverviewElement = this.$el.querySelector('.contact-overview');
        if (contactOverviewElement) {
          contactOverviewElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    cancelForm() {
      this.$refs.contact.reset();
      if (this.selectedContactId) {
        this.populateForm();
      } else {
        this.showForm = false;
      }
    },
    enableEdit() {
      this.isReadOnly = false;
    },
    createContact() {
      const project_id = this.$route.params.id;
      this.isLoading = true;
      if (this.checkValidations() === false) { return }

      if (project_id) {
        this.$store.dispatch('fulfill_integrate/get_project_selected', { project_id })
          .then((response) => {
            if (this.locked(response.project)) {
              this.$router.push('/applications?errorPersist=true');
              return;
            } else {
              this.processContactForm();
            }
          })
          .catch((error) => {
            this.isVisible = false;
            console.log(error);
            this.isLoading = false;
          });
      } else {
        this.processContactForm();
      }
    },

    checkValidations() {
      const isFormValid = this.contactFields.every(field => {
        if (field.required === 'true') {
          const skipFields = ['MobilePhone', 'OtherPhone'];
          const contactFieldsSetting = this.fulfill_sync_details.settings?.contact_fields;

          if (contactFieldsSetting) {
            if (contactFieldsSetting.use_educate_validation) {
              if (skipFields.includes(field.name)) {
                return true;
              } else {
                return this.newContact[field.name] && this.newContact[field.name].trim() !== '';
              }
            } else {
              return this.newContact[field.name] && this.newContact[field.name].trim() !== '';
            }
          } else {
            return this.newContact[field.name] && this.newContact[field.name].trim() !== '';
          }
        }
        return true;
      });

      const emailField = this.contactFields.find(field => field.type === 'EMAIL');
      if (emailField) {
        const emailValue = this.newContact[emailField.name];
        if (emailValue) {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(emailValue)) {
            this.$notify({
              group: 'notice-stay',
              type: 'error',
              text: 'Please enter a valid email address.',
            });

            this.isLoading = false;
            return false;
          }
        }
      }

      if (!isFormValid) {
        this.$refs.contact.validate();

        this.$notify({
          group: 'notice-stay',
          type: 'error',
          text: 'Please fill in all required fields.',
        });

        this.$nextTick(() => {
          const hasError = this.$el.querySelector('.has-error');
          if (hasError) {
            hasError.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });

        this.isLoading = false;
        return false;
      }
    },

    processContactForm() {
      this.isLoading = true;
      const formattedContact = {
        fields: Object.keys(this.newContact).map(key => ({
          name: key,
          value: this.newContact[key],
        })),
      };

      if (this.selectedContactId) {
        const updatePayload = {
          Id: this.selectedContactId,
          fields: formattedContact.fields,
        };
        this.$store.dispatch('contacts/updateContact', updatePayload)
          .then(() => {
            this.$store.dispatch('contacts/fetchContacts').then(() => {
              this.populateForm();
              this.$notify({
                group: 'notice',
                text: 'Contact Updated successfully',
              });
              this.isReadOnly = true;
            });
          })
          .catch((errors) => {
            console.log(errors);
            this.$notify({
              group: 'notice-stay',
              type: 'error',
              text: errors.response.data.errors,
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$store.dispatch('contacts/createContact', formattedContact)
          .then((createdContact) => {
            this.selectedContactId = createdContact[0].id;
            this.showForm = true;
            this.isReadOnly = true;
            this.$notify({
              group: 'notice',
              text: 'Contact Created successfully',
            });
          })
          .catch((errors) => {
            console.log(errors);
            this.$notify({
              group: 'notice-stay',
              type: 'error',
              text: errors.response.data.errors,
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    updateFieldsAttributes(fields) {
      const fieldsToRemove = ['Email', 'OtherPhone', 'MobilePhone'];
      const updatedFields = fields.map(currentField => {
        if (!this.fulfill_sync_details.settings?.contact_fields || this.fulfill_sync_details.settings.contact_fields?.use_educate_validation) {
          const field = fieldsToRemove.find(fieldName => fieldName === currentField.name);
          if (field) {
            currentField = {
              ...currentField,
              required: 'false',
            };
          } else {
            currentField = {
              ...currentField,
            };
          }
          if (currentField.name === 'MailingPostalCode') {
            currentField = {
              ...currentField,
              label: 'Mailing Postal Code',
            };
          }
        }
        return currentField;
      });
      return updatedFields;
    },

    addValidation(listItem) {
      const validationList = [];

      if (listItem.required === 'true') {
        validationList.push('required');
      }

      if (listItem.type === 'EMAIL') {
        validationList.push('email');
      }

      return validationList.join('|');
    },
    formatPhone(fieldName) {
      const phone = this.newContact[fieldName].replace(/\D/g, '');
      const formattedPhone = phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
      this.newContact[fieldName] = formattedPhone.substring(0, 12);
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '../assets/stylesheets/components/project/contact.scss';

.input-field {
  font-size: 1.25rem;
  line-height: 1.5;
}

.input-text.box-alert,
.input-field.box-alert {
  background-color: rgba(255, 0, 0, 0.2) !important;
  border: 1px solid #dc3545 !important;
}
</style>
